"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Code2,
  FileText,
  Headphones,
  Info,
  Loader2,
  Lock,
  Plus,
  Search,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react";
import { UsersLayout } from "@/components/pages/users-layout";
import { UsersRowMenu } from "@/components/users-row-menu";
import { Card, Pill } from "@/components/pulse-shell";
import { Select } from "@/components/ui/select";
import { api, ApiError, type PlatformRole } from "@/lib/api";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import { defaultAdminPath } from "@/components/pulse-shell";
import { isAdministratorProfile } from "@/lib/role-impersonation";
import { formatShortDate } from "@/lib/users-format";
import { validateAddRoleForm } from "@/lib/users-nav";
import { trapFocus } from "@/lib/users-focus-trap";

function roleIcon(name: string, type: string) {
  const n = name.toLowerCase();
  if (n.includes("admin")) return Shield;
  if (n.includes("operator")) return Headphones;
  if (n.includes("read")) return FileText;
  if (n.includes("quality") || n.includes("analyst")) return ShieldCheck;
  if (n.includes("dev")) return Code2;
  if (type === "SYSTEM") return Shield;
  return Users;
}

export function UsersRolesPage() {
  const navigate = useNavigate();
  const me = useAuthProfile();
  const { realProfile, beginImpersonation } = useRoleImpersonation();
  const canManage = Boolean(me?.permissions?.includes("roles.manage"));
  const canImpersonate = isAdministratorProfile(realProfile);
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [type, setType] = useState("All role types");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const searchTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const typeParam =
        type === "All role types"
          ? undefined
          : type === "System"
            ? "SYSTEM"
            : type === "Custom"
              ? "CUSTOM"
              : undefined;
      const out = await api.roles.list({
        q: q || undefined,
        type: typeParam,
        limit,
        offset,
      });
      setRoles(out.roles || []);
      setTotal(out.total || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load roles");
      setRoles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, type, limit, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      setOffset(0);
      setQ(searchInput.trim());
    }, 280);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const rangeLabel =
    total === 0
      ? "0 of 0"
      : `${offset + 1}-${Math.min(offset + roles.length, total)} of ${total}`;

  async function removeRole(roleId: string) {
    if (!window.confirm("Delete this custom role? Users must be reassigned first.")) return;
    setMenuFor(null);
    try {
      await api.roles.remove(roleId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function impersonateRole(role: PlatformRole) {
    setMenuFor(null);
    if (!canImpersonate || !beginImpersonation) return;
    try {
      // Role list may omit full permission arrays — load detail when needed.
      let permissions = role.permissions || [];
      let allowedAgentIds = role.allowedAgentIds || [];
      if (!permissions.length) {
        const detail = await api.roles.get(role.roleId);
        permissions = detail.role.permissions || [];
        allowedAgentIds = detail.role.allowedAgentIds || [];
      }
      beginImpersonation({
        roleId: role.roleId,
        name: role.name,
        permissions,
        allowedAgentIds,
      });
      navigate(defaultAdminPath(permissions), { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start role preview");
    }
  }

  return (
    <UsersLayout>
      <Card
        className="users-card"
        title="Roles & Permissions"
        kicker={`${total} role${total === 1 ? "" : "s"}`}
        action={
          canManage ? (
            <button type="button" className="btn primary" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Add role
            </button>
          ) : null
        }
      >
        <div className="users-info-banner roles-banner">
          <Info size={16} />
          <span>System roles are managed by Pulse and cannot be edited or deleted.</span>
        </div>
        <div className="users-toolbar">
          <label className="users-search">
            <Search size={15} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search roles"
              aria-label="Search roles"
            />
          </label>
          <Select
            searchable={false}
            ariaLabel="Role type filter"
            value={type}
            onChange={(v) => {
              setOffset(0);
              setType(v || "All role types");
            }}
            options={["All role types", "System", "Custom"].map((x) => ({
              value: x,
              label: x,
            }))}
          />
          <div className="users-adv-wrap">
            <button
              type="button"
              className="icon-button"
              aria-label="Advanced filters"
              aria-expanded={advOpen}
              onClick={() => setAdvOpen((o) => !o)}
            >
              <SlidersHorizontal size={16} />
            </button>
            {advOpen && (
              <div className="users-adv-popover" role="dialog">
                <div className="mini-label">Rows per page</div>
                <Select
                  searchable={false}
                  ariaLabel="Rows per page"
                  value={String(limit)}
                  onChange={(v) => {
                    setOffset(0);
                    setLimit(Number(v || 10));
                  }}
                  options={["10", "25", "50"].map((x) => ({ value: x, label: x }))}
                />
                <button type="button" className="btn ghost" onClick={() => setAdvOpen(false)}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="users-error" role="alert">
            {error}
          </div>
        )}

        <div className="users-table-wrap">
          <table className="users-table roles-table">
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Type</th>
                <th scope="col">Users</th>
                <th scope="col">Permission scope</th>
                <th scope="col">Last updated</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="users-empty">
                    <Loader2 className="spin" size={18} /> Loading roles…
                  </td>
                </tr>
              )}
              {!loading && roles.length === 0 && (
                <tr>
                  <td colSpan={6} className="users-empty">
                    No roles match these filters.
                  </td>
                </tr>
              )}
              {!loading &&
                roles.map((r) => {
                  const Icon = roleIcon(r.name, r.type);
                  const isSystem = r.type === "SYSTEM" || r.systemImmutable;
                  return (
                    <tr key={r.roleId}>
                      <td>
                        <div className="users-person">
                          <span className="users-avatar role-avatar" aria-hidden>
                            <Icon size={16} />
                          </span>
                          <span className="users-person-meta">
                            <b>
                              {r.name}
                              {isSystem && <Lock size={12} className="role-lock" aria-label="Locked" />}
                            </b>
                          </span>
                        </div>
                      </td>
                      <td>
                        <Pill tone={isSystem ? "gray" : "purple"}>
                          {isSystem ? "System role" : "Custom role"}
                        </Pill>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() =>
                            navigate(`/users/all?roleId=${encodeURIComponent(r.roleId)}`)
                          }
                        >
                          {r.userCount ?? 0} user{(r.userCount ?? 0) === 1 ? "" : "s"}
                        </button>
                      </td>
                      <td>
                        <div className="scope-pills">
                          {(r.permissionScope || []).map((p) => (
                            <Pill key={p} tone="gray">
                              {p}
                            </Pill>
                          ))}
                        </div>
                      </td>
                      <td>{formatShortDate(r.updatedAt)}</td>
                      <td className="users-row-actions">
                        <UsersRowMenu
                          label={`Actions for ${r.name}`}
                          open={menuFor === r.roleId}
                          onOpenChange={(open) => setMenuFor(open ? r.roleId : null)}
                          items={[
                            {
                              key: isSystem ? "view" : "edit",
                              label: isSystem ? "View" : "Edit",
                              onSelect: () => navigate(`/users/roles/${r.roleId}`),
                            },
                            ...(canImpersonate
                              ? [
                                  {
                                    key: "impersonate",
                                    label: "Impersonate Role",
                                    onSelect: () => void impersonateRole(r),
                                  },
                                ]
                              : []),
                            ...(!isSystem && canManage
                              ? [
                                  {
                                    key: "delete",
                                    label: "Delete",
                                    danger: true,
                                    onSelect: () => void removeRole(r.roleId),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="users-pagination">
          <span>{rangeLabel}</span>
          <div className="users-pagination-right">
            <span className="muted">Rows per page</span>
            <Select
              size="sm"
              searchable={false}
              ariaLabel="Rows per page"
              value={String(limit)}
              onChange={(v) => {
                setOffset(0);
                setLimit(Number(v || 10));
              }}
              options={["10", "25", "50"].map((x) => ({ value: x, label: x }))}
            />
          </div>
        </div>
      </Card>

      {addOpen && (
        <AddRoleModal
          templateRoles={roles}
          onClose={() => setAddOpen(false)}
          onCreated={(roleId) => {
            setAddOpen(false);
            navigate(`/users/roles/${roleId}`);
          }}
        />
      )}
    </UsersLayout>
  );
}

function AddRoleModal({
  templateRoles,
  onClose,
  onCreated,
}: {
  templateRoles: PlatformRole[];
  onClose: () => void;
  onCreated: (roleId: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateRoleId, setTemplateRoleId] = useState("none");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const release = dialogRef.current ? trapFocus(dialogRef.current) : () => undefined;
    return () => {
      window.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  const templateOpts = useMemo(
    () => [
      { value: "none", label: "No template" },
      ...templateRoles.map((r) => ({ value: r.roleId, label: r.name })),
    ],
    [templateRoles],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const validationError = validateAddRoleForm({ name });
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const out = await api.roles.create({
        name: name.trim(),
        description: description.trim() || undefined,
        templateRoleId: templateRoleId === "none" ? null : templateRoleId,
      });
      onCreated(out.role.roleId);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Failed to add role");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="users-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="users-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-role-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="users-modal-head">
          <div>
            <h2 id="add-role-title">Add role</h2>
            <p>Create a custom role, then configure its permissions.</p>
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)}>
          <label className="users-modal-full">
            <span>
              Role name <span aria-hidden>*</span>
            </span>
            <input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="users-modal-full">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>
          <label className="users-modal-full">
            Start from a template
            <Select
              searchable
              ariaLabel="Template"
              value={templateRoleId}
              onChange={(v) => setTemplateRoleId(v || "none")}
              options={templateOpts}
            />
          </label>
          <div className="users-info-banner">
            <Info size={16} />
            <span>Detailed permissions are configured on the next screen after the role is created.</span>
          </div>
          {error && (
            <div className="users-error" role="alert">
              {error}
            </div>
          )}
          <div className="users-modal-foot">
            <button type="button" className="btn ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? "Creating…" : "Add role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
